// src/pages/Learning/Resources.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppScreenShell from "../../components/layout/AppScreenShell";
import Banner from "../../components/layout/Banner";
import WeeklyFinancialCheckIn from "../../components/check-ins/WeeklyFinancialCheckIn";
import {
  buildWeeklyCheckInHistory,
  canEditWeeklyCheckIn,
  formatWeeklyCheckInDate,
  getMyWeeklyFinancialCheckIns,
  getOrStartMyWeeklyCheckInTracking,
} from "../../lib/check-ins/weeklyCheckIn";
import {
  getMyCalendarReminders,
  getMyCompletedFinancialActions,
  completeMyActionOccurrence,
} from "../../lib/actions/calendarSetup";
import { supabase } from "../../supabaseClient";
import SubscriptionPopup from "../../components/subscriptions/SubscriptionPopup";
import { checkPaidJourneyAccessForCurrentUser } from "../../lib/access/paidJourneyAccess";

const TOOLS_RESOURCES_BOOK_SRC =

  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/Gwin-Book.png";

const TOOLS_RESOURCES_PEEK_SRC =

  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Public%20Pages/Landing/gwin-penguin.png";



const ICONS = {

  xp: "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Bolt_White.png",

  task: "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Action_Orange.png",

  concept:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Brain_Orange.png",

  template:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Objective_orange.png",

  locked:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/Lock_Orange.png",

  unlocked:

    "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Core%20Images/celebrate_orange.png",

};

const FUNDING_DISCLAIMER =

  "This resource supports readiness. It does not guarantee funding.";

const TEMPLATE_UNLOCKS = [

  {

    id: "u5",

    level: 5,

    requiredQuest: 1,

    title: "Weekly Money Check Tracker",

    description:

      "Unlocked after completing Level 5. Use this template to support your business finance journey.",

    url: "https://docs.google.com/spreadsheets/d/1ibkgODVA8ef1q7H1p8AFqqIqi54RRKN6/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

  },

  {

    id: "u8",

    level: 8,

    requiredQuest: 1,

    title: "Business vs Personal Spending Rules Sheet",

    description:

      "Unlocked after completing Level 8. Use this template to strengthen your financial habits.",

    url: "https://docs.google.com/spreadsheets/d/1QVBe5-8W1xj4yMJMBZ9Yjd_bThj4qfCW/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

  },

  {

    id: "u14",

    level: 14,

    requiredQuest: 2,

    title: "Simple Transaction Tracker / Basic Chart of Accounts Template",

    description:

      "Unlocked after completing Level 14. Use this template as part of your funding-readiness journey.",

    url: "https://docs.google.com/spreadsheets/d/1FYXdjUaX1rEXeTqTvoijXWBfdBo2oURE/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

  },

  {

    id: "u17",

    level: 17,

    requiredQuest: 2,

    title: "Invoice, Receipt, Quote and Supplier Statement Checklist",

    description:

      "Unlocked after completing Level 17. Use this template to keep building your business finance toolkit.",

    url: "https://docs.google.com/spreadsheets/d/16es98BLsDaJmvMdh5q5PN-czwg8Q4MNb/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

  },

  {

    id: "u20",

    level: 20,

    requiredQuest: 3,

    title: "Basic Bank Reconciliation Template",

    description:

      "Unlocked after completing Level 20. Use this template to support your next stage of business finance planning.",

    url: "https://docs.google.com/spreadsheets/d/1hMSi1p7_k7x4XKatz7XeWxrnxIzCr4KB/edit?usp=sharing&ouid=108340782551691965931&rtpof=true&sd=true",

  },

];

function IconImg({ src, className = "h-4 w-4 object-contain" }) {

  if (!src) return null;

  return (

    <img

      src={src}

      alt=""

      aria-hidden="true"

      className={className}

      loading="lazy"

      decoding="async"

      draggable="false"

    />

  );

}

function normalizeQuestKey(qid) {

  if (!qid) return "";

  if (qid === "financial-foundations" || qid === "quest-1" || qid === "Q1") {

    return "1";

  }

  if (qid === "quest-2" || qid === "Q2") return "2";

  if (qid === "quest-3" || qid === "Q3") return "3";

  if (qid === "quest-4" || qid === "Q4") return "4";

  if (qid === "quest-5" || qid === "Q5") return "5";

  return String(qid);

}

function normalizeLevelKey(levelKey) {

  if (!levelKey) return "";

  const s = String(levelKey).trim();

  if (/^L\d+$/i.test(s)) return s.toUpperCase();

  if (/^\d+$/.test(s)) return `L${s}`;

  const match = s.match(/(\d+)/);

  return match ? `L${match[1]}` : s.toUpperCase();

}



function getQuestNumber(value) {

  const normalized = normalizeQuestKey(value);

  const n = Number.parseInt(normalized, 10);

  return Number.isFinite(n) ? n : null;

}

function isPaidJourneyQuest(questId) {

  const questNumber = getQuestNumber(questId);

  return questNumber >= 2 && questNumber <= 5;

}

function isPaidLockedQuest(questId, hasPaidJourneyAccess) {

  return isPaidJourneyQuest(questId) && hasPaidJourneyAccess !== true;

}

function EmptyState({ icon, title, description }) {

  return (

    <div className="rounded-3xl border border-dashed border-[#E5E5E5] bg-white p-8 text-center">

      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC]">

        <IconImg src={icon} className="h-7 w-7 object-contain" />

      </div>

      <h3 className="mt-4 text-lg font-bold text-[#333333]">{title}</h3>

      <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-[#757575]">

        {description}

      </p>

    </div>

  );

}

function TemplateCard({ item, unlocked, paidLocked, onBuy }) {

  return (

    <div

      className={[

        "relative overflow-hidden rounded-3xl border p-4",

        unlocked

          ? "border-[#EFEFEF] bg-white shadow-sm"

          : "border-[#EFEFEF] bg-[#F8F8F8]",

      ].join(" ")}

    >

      {paidLocked && (

        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 px-4 backdrop-blur-[2px]">

          <div className="w-full max-w-[260px] rounded-2xl bg-white p-4 text-center shadow-lg border border-orange-100">

            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF4EC]">

              <IconImg src={ICONS.locked} className="h-5 w-5 object-contain" />

            </div>

            <p className="text-sm font-black text-[#333333]">

              Paid journey template

            </p>

            <p className="mt-1 text-xs font-semibold leading-relaxed text-[#757575]">

              Unlock the 60-day Business Finance Journey to access this

              template.

            </p>

            <button

              type="button"

              onClick={onBuy}

              className="mt-3 rounded-full bg-[#FF914D] px-4 py-2 text-xs font-bold text-white hover:bg-[#E9782F]"

            >

              Unlock journey

            </button>

          </div>

        </div>

      )}

      <div className={paidLocked ? "blur-[1.5px]" : ""}>

        <div className="flex items-start gap-3">

          <div

            className={[

              "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",

              unlocked ? "bg-[#FFF4EC]" : "bg-white",

            ].join(" ")}

          >

            <IconImg

              src={unlocked ? ICONS.unlocked : ICONS.locked}

              className="h-6 w-6 object-contain"

            />

          </div>

          <div className="min-w-0 flex-1">

            <div className="flex flex-wrap items-center gap-2">

              <span className="text-[10px] font-semibold text-[#757575]">

                Level {item.level}

              </span>

              <span

                className={[

                  "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold",

                  unlocked

                    ? "bg-[#FFF4EC] text-[#FF914D]"

                    : "bg-[#EFEFEF] text-[#757575]",

                ].join(" ")}

              >

                {paidLocked ? "Paid Locked" : unlocked ? "Unlocked" : "Locked"}

              </span>

            </div>

            <p

              className={[

                "mt-1 text-base font-bold leading-tight",

                unlocked ? "text-[#333333]" : "text-[#757575]",

              ].join(" ")}

            >

              {item.title}

            </p>

            <p className="mt-1 text-xs leading-relaxed text-[#757575]">

              {item.description}

            </p>

            {item.fundingRelated && (

              <div className="mt-3 rounded-2xl border border-[#FFD7BF] bg-[#FFF4EC] px-3 py-2">

                <p className="text-[11px] font-semibold leading-snug text-[#9A4A12]">

                  {FUNDING_DISCLAIMER}

                </p>

              </div>

            )}

            <div className="mt-3">

              {unlocked && !paidLocked ? (

                <a

                  href={item.url}

                  target="_blank"

                  rel="noopener noreferrer"

                  className="inline-flex rounded-full bg-[#FF914D] px-4 py-2 text-xs font-bold text-white hover:bg-[#E9782F]"

                >

                  Open template

                </a>

              ) : paidLocked ? (

                <span className="text-xs font-semibold text-[#757575]">

                  Unlock the 60-day journey to access

                </span>

              ) : (

                <span className="text-xs font-semibold text-[#757575]">

                  Reach Level {item.level} to unlock

                </span>

              )}

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}

function getValidResourceTab(value) {

  return ["tasks", "templates", "checkins"].includes(value)

    ? value

    : "tasks";

}

function FinancialActionCard({

  action,

  completed = false,

  onOpen,

  onComplete,

  completing = false,

}) {

  const isCalendarSetup =

    action.definition_code === "SYSTEM_CALENDAR_SETUP" ||

    action.action_type === "calendar_setup";

  const completedDate = action.completed_at

    ? new Date(action.completed_at).toLocaleDateString(

        "en-ZA",

        {

          day: "numeric",

          month: "short",

          year: "numeric",

          timeZone: "Africa/Johannesburg",

        }

      )

    : null;

  return (

    <div className="rounded-3xl border border-[#EFEFEF] bg-white p-4 shadow-sm">

      <div className="flex items-start gap-3">

        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF4EC]">

          <IconImg

            src={completed ? ICONS.unlocked : ICONS.task}

            className="h-5 w-5 object-contain"

          />

        </div>

        <div className="min-w-0 flex-1">

          <span

            className={[

              "inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold",

              completed

                ? "bg-[#E8F8EE] text-green-700"

                : "bg-[#FFF4EC] text-[#FF914D]",

            ].join(" ")}

          >

            {completed

              ? "Completed"

              : action.status === "overdue"

              ? "Overdue"

              : action.status === "upcoming"

              ? "Upcoming"

              : "Open"}

          </span>

          <p className="mt-2 text-base font-bold text-[#1D1B20]">

            {action.title ||

              (isCalendarSetup

                ? "Financial Calendar Setup"

                : "Financial Action")}

          </p>

          <p className="mt-1 text-xs leading-relaxed text-[#757575]">

            {action.description ||

              (isCalendarSetup

                ? "Configure your business financial calendar."

                : "Complete this financial action.")}

          </p>

          <p className="mt-2 text-[11px] text-[#898989]">

            Financial action

            {completedDate

              ? ` · Completed ${completedDate}`

              : ""}

          </p>

          {!completed && (

            <div className="mt-3 flex flex-wrap gap-2">

              {action.route && (

                <button

                  type="button"

                  onClick={() => onOpen(action)}

                  className="rounded-full border border-[#FF914D] px-4 py-2 text-xs font-bold text-[#FF914D]"

                >

                  {isCalendarSetup

                    ? "SET UP CALENDAR"

                    : "VIEW ACTION"}

                </button>

              )}

              {!isCalendarSetup && (
  <div className="mt-4">
    <p className="mb-3 text-sm font-semibold text-[#333333]">
      Have you completed this action?
    </p>

    <div className="flex gap-3">
      <button
        type="button"
        disabled={completing}
        onClick={() => onComplete(action)}
        className="flex-1 rounded-xl bg-[#FF914D] px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
      >
        {completing ? "SAVING..." : "YES"}
      </button>

      <button
        type="button"
        disabled={completing}
        onClick={() => {}}
        className="flex-1 rounded-xl border border-[#BCBCBC] bg-white px-4 py-3 text-sm font-bold text-[#757575]"
      >
        NO
      </button>
    </div>
  </div>
)}

            </div>

          )}

        </div>

      </div>

    </div>

  );

}


// A calendar month is interpreted in South African time, not browser-local time.
function saDayKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function addBusinessDays(yyyyMmDd, count) {
  const date = new Date(`${yyyyMmDd}T12:00:00Z`);
  let remaining = count;
  while (remaining > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6) remaining -= 1;
  }
  return date.toISOString().slice(0, 10);
}

// Display-only grace handling: keep the original due_at and stored status intact.
// Only weekends are excluded here. Public-holiday handling must be aligned with
// the authoritative Supabase calendar rules before treating this as a statutory rule.
function calendarDisplayStatus(action, todayKey) {
  if (action.status === "completed") return "completed";
  const due = saDayKey(action.due_at);
  if (!due) return action.status;
  if (todayKey < due) return "upcoming";
  return todayKey > addBusinessDays(due, 3) ? "overdue" : "due";
}

export default function Resources() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => getValidResourceTab(searchParams.get("tab")));
  const [financialActions, setFinancialActions] = useState([]);
  const [completedFinancialActions, setCompletedFinancialActions] = useState([]);
  const [financialActionsLoading, setFinancialActionsLoading] = useState(true);
  const [financialActionsError, setFinancialActionsError] = useState("");
  const [completingFinancialActionId, setCompletingFinancialActionId] = useState(null);
  const [financialActionsRefreshKey, setFinancialActionsRefreshKey] = useState(0);

  const [weeklyHistory, setWeeklyHistory] = useState([]);
  const [weeklyHistoryLoading, setWeeklyHistoryLoading] = useState(false);
  const [weeklyHistoryError, setWeeklyHistoryError] = useState("");
  const [weeklyHistoryRefreshKey, setWeeklyHistoryRefreshKey] = useState(0);
  const [weeklyCheckInOpen, setWeeklyCheckInOpen] = useState(false);
  const [selectedWeeklyCheckInWeek, setSelectedWeeklyCheckInWeek] = useState(null);
  const [search, setSearch] = useState("");
  const [completedLessonKeys, setCompletedLessonKeys] = useState(new Set());
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [paidJourneyAccess, setPaidJourneyAccess] = useState({ checked: false, allowed: false });
  const hasPaidJourneyAccess = paidJourneyAccess.allowed === true;

  const todayKey = saDayKey(new Date());
  const currentMonthFinancialActions = useMemo(() => {
    const month = todayKey.slice(0, 7);
    return financialActions
      .filter((item) => {
        if (!item?.due_at || !["upcoming", "due", "overdue"].includes(item.status)) return false;
        if (item.action_type === "calendar_setup" || item.source_key === "SYSTEM_CALENDAR_SETUP") return false;
        if (item.source_type === "learning_level" || String(item.source_key || "").startsWith("LEARNING:")) return false;
        return saDayKey(item.due_at)?.slice(0, 7) === month;
      })
      .map((item) => ({ ...item, status: calendarDisplayStatus(item, todayKey) }))
      .sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
  }, [financialActions, todayKey]);

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    return TEMPLATE_UNLOCKS
      .map((item) => ({
        ...item,
        paidLocked: isPaidLockedQuest(item.requiredQuest || 1, hasPaidJourneyAccess),
      }))
      .filter((item) => !query || `${item.title} ${item.description}`.toLowerCase().includes(query));
  }, [search, hasPaidJourneyAccess]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const [lessons, progress] = await Promise.all([
          supabase.from("lessons").select("id, quest_id, level_key"),
          supabase.from("lesson_progress").select("lesson_id, completed").eq("user_id", user.id),
        ]);
        if (lessons.error) throw lessons.error;
        if (progress.error) throw progress.error;
        const doneIds = new Set((progress.data || []).filter((p) => p.completed === true).map((p) => p.lesson_id));
        const done = new Set((lessons.data || []).filter((l) => doneIds.has(l.id))
          .map((l) => `${normalizeQuestKey(l.quest_id)}:${normalizeLevelKey(l.level_key)}`));
        if (alive) setCompletedLessonKeys(done);
      } catch (error) { console.warn("Template unlock progress could not be loaded:", error); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setTab(getValidResourceTab(searchParams.get("tab")));
  }, [searchParams]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const result = await checkPaidJourneyAccessForCurrentUser({ questId: 2, levelNumber: 14 });
        if (alive) setPaidJourneyAccess({ checked: true, ...result });
      } catch (error) {
        console.warn("Paid journey access could not be checked:", error);
        if (alive) setPaidJourneyAccess({ checked: true, allowed: false, reason: "access_check_failed" });
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (tab !== "checkins") return;
    let alive = true;
    (async () => {
      try {
        setWeeklyHistoryLoading(true);
        setWeeklyHistoryError("");
        const trackingStartWeek = await getOrStartMyWeeklyCheckInTracking();
        const submissions = await getMyWeeklyFinancialCheckIns();
        if (alive) setWeeklyHistory(buildWeeklyCheckInHistory(submissions, trackingStartWeek, new Date()));
      } catch (error) {
        console.error("Could not load weekly check-in history:", error);
        if (alive) { setWeeklyHistoryError(error?.message || "Could not load your check-ins."); setWeeklyHistory([]); }
      } finally {
        if (alive) setWeeklyHistoryLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tab, weeklyHistoryRefreshKey]);

  useEffect(() => {
    if (tab !== "tasks") return;
    let alive = true;
    (async () => {
      try {
        setFinancialActionsLoading(true);
        setFinancialActionsError("");
        const [reminders, completed] = await Promise.all([
          getMyCalendarReminders(),
          getMyCompletedFinancialActions(),
        ]);
        if (!alive) return;
        setFinancialActions(Array.isArray(reminders) ? reminders : []);
        setCompletedFinancialActions(Array.isArray(completed) ? completed : []);
      } catch (error) {
        console.error("Could not load financial actions:", error);
        if (alive) {
          setFinancialActionsError(error?.message || "Could not load financial actions.");
          setFinancialActions([]);
          setCompletedFinancialActions([]);
        }
      } finally {
        if (alive) setFinancialActionsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tab, financialActionsRefreshKey]);

  async function handleCompleteFinancialAction(action) {
    if (!action?.id || completingFinancialActionId || action.action_type === "calendar_setup") return;
    try {
      setCompletingFinancialActionId(action.id);
      setFinancialActionsError("");
      await completeMyActionOccurrence(action.id);
      setFinancialActionsRefreshKey((value) => value + 1);
    } catch (error) {
      console.error("Could not complete financial action:", error);
      setFinancialActionsError(error?.message || "Could not complete this financial action.");
    } finally { setCompletingFinancialActionId(null); }
  }

  const handleTabChange = (value) => {
    const next = getValidResourceTab(value);
    setTab(next);
    setSearch("");
    setSearchParams({ tab: next }, { replace: true });
  };

return (

  <>

    <AppScreenShell

      showHeader={false}

      showPenguinBackground={false}

      showBottomBar={false}

      maxWidth="max-w-lg"

      contentClassName="!px-0 !pt-0"

    >

      <Banner homeTo="/dashboard" />

      <div className="relative min-h-[calc(100vh-74px)] overflow-hidden bg-[#F8F8F8] pb-28">

        <section className="relative overflow-hidden bg-white px-6 pb-8 pt-8">

          <div className="relative min-h-[225px]">

            <div className="relative z-10 w-[56%] pt-10">

              <h1 className="text-[27px] font-black leading-[1.02] tracking-[-0.045em] text-[#111111]">

                Tools and

                <br />

                Resources

              </h1>

              <p className="mt-4 max-w-[220px] text-[10px] font-medium leading-[1.55] text-[#858585]">

                View your monthly actions, weekly check-ins and access

                unlocked templates and guides.

              </p>

            </div>

            <img

              src={TOOLS_RESOURCES_BOOK_SRC}

              alt=""

              aria-hidden="true"

              className="pointer-events-none absolute right-[-4px] top-[10px] h-[178px] w-[188px] object-contain"

              draggable="false"

            />

          </div>

          <div className="relative z-20 mt-1 grid grid-cols-3 gap-2">

            {[

              ["tasks", "Open actions"],

              ["templates", "Templates"],

              ["checkins", "Check-ins"],

            ].map(([value, label]) => {

              const active = tab === value;

              return (

                <button

                  key={value}

                  type="button"

                  onClick={() => handleTabChange(value)}

                  className={[

                    "flex min-h-[52px] min-w-0 items-center justify-center rounded-full px-1.5 text-center text-[10px] font-bold transition",

                    active

                      ? "bg-[#FF914D] text-white"

                      : "bg-[#D4D4D4] text-[#727272]",

                  ].join(" ")}

                >

                  {label}

                </button>

              );

            })}

          </div>

        </section>

        <img

          src={TOOLS_RESOURCES_PEEK_SRC}

          alt=""

          aria-hidden="true"

          className="pointer-events-none absolute bottom-[-22px] right-[-88px] z-0 w-[245px] max-w-none -rotate-[37deg]"

          draggable="false"

        />

        <div className="relative z-10 px-5 pb-8 pt-6">

{tab === "tasks" && (
          <section className="mt-5 space-y-6">
            {financialActionsError && (
              <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                {financialActionsError}
              </p>
            )}

            <div>
              <h2 className="mb-3 text-lg font-bold text-[#1D1B20]">
                Open Actions This Month ({currentMonthFinancialActions.length})
              </h2>
              {financialActionsLoading ? (
                <p className="text-sm text-[#757575]">Loading financial actions...</p>
              ) : currentMonthFinancialActions.length > 0 ? (
                <div className="space-y-3">
                  {currentMonthFinancialActions.map((action) => (
                    <FinancialActionCard
                      key={`financial-${action.id}`}
                      action={action}
                      onOpen={(item) => {
                        if (item.route) navigate(item.route);
                      }}
                      onComplete={handleCompleteFinancialAction}
                      completing={completingFinancialActionId === action.id}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={ICONS.task}
                  title="You're all caught up"
                  description="No open financial actions for this month."
                />
              )}
            </div>

            <div>
              <h2 className="mb-3 text-lg font-bold text-[#1D1B20]">
                Completed Actions ({completedFinancialActions.length})
              </h2>
              {financialActionsLoading ? (
                <p className="text-sm text-[#757575]">Loading completed financial actions...</p>
              ) : completedFinancialActions.length > 0 ? (
                <div className="space-y-3">
                  {completedFinancialActions.map((action) => (
                    <FinancialActionCard
                      key={`completed-financial-${action.id}`}
                      action={action}
                      completed
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#757575]">No completed financial actions yet.</p>
              )}
            </div>
          </section>
        )}


        {tab === "templates" && (
          <section className="mt-5 space-y-4">
            <h2 className="text-lg font-black text-[#1D1B20]">Templates</h2>
            <input
              type="search"
              aria-label="Search templates"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search templates"
              className="w-full rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm outline-none focus:border-[#FF914D]"
            />
            {filteredTemplates.map((item) => (
              <TemplateCard
                key={item.id}
                item={item}
                unlocked={completedLessonKeys.has(`${item.requiredQuest}:${normalizeLevelKey(item.level)}`)}
                paidLocked={item.paidLocked}
                onBuy={() => setShowSubscriptionPopup(true)}
              />
            ))}
          </section>
        )}
{tab === "checkins" && (

  <section className="mt-5 space-y-4">

    <div>

      <h2 className="text-lg font-black text-[#1D1B20]">

        Weekly Financial Check-Ins

      </h2>

      <p className="mt-1 text-xs leading-relaxed text-[#757575]">

        Track your weekly sales, cash received, spending

        and closing cash balance.

      </p>

    </div>

    {weeklyHistoryLoading && (

      <p className="py-6 text-center text-sm text-[#757575]">

        Loading your financial check-ins...

      </p>

    )}

    {!weeklyHistoryLoading && weeklyHistoryError && (

      <div className="rounded-xl border border-red-200 bg-red-50 p-4">

        <p className="text-xs text-red-700">

          {weeklyHistoryError}

        </p>

        <button

          type="button"

          onClick={() =>

            setWeeklyHistoryRefreshKey((value) => value + 1)

          }

          className="mt-3 text-xs font-bold text-red-700 underline"

        >

          Try again

        </button>

      </div>

    )}

    {!weeklyHistoryLoading &&

      !weeklyHistoryError &&

      weeklyHistory.length === 0 && (

        <div className="rounded-2xl border border-[#EFEFEF] bg-white p-5">

          <p className="text-sm text-[#757575]">

            No weekly financial check-ins to display yet.

          </p>

        </div>

      )}

    {!weeklyHistoryLoading &&

      !weeklyHistoryError &&

      weeklyHistory.map((week) => {

const submission = week.submission;

const isCompleted = week.status === "completed";

const isMissed = week.status === "missed";

const canSubmit =

  canEditWeeklyCheckIn(week.week_start);



const badgeClass = isCompleted

  ? "bg-[#E8F8EE] text-[#247A52]"

  : isMissed

  ? "bg-[#FFE9EA] text-[#D2454C]"

  : week.status === "overdue"

  ? "bg-[#FFF0E6] text-[#D46A21]"

  : week.status === "upcoming"

  ? "bg-[#EFEFEF] text-[#757575]"

  : "bg-[#FFF0E6] text-[#FF7A31]";

const statusLabel = {

  completed: "Completed",

  missed: "Missed",

  upcoming: "Upcoming",

  due: "Due today",

  overdue: "Overdue",

}[week.status] || week.status;

        return (

          <article

            key={week.week_start}

            className="rounded-[22px] border border-[#E8E8E8] bg-white p-4 shadow-sm"

          >

            <div className="flex items-start justify-between gap-3">

              <div>

                <h3 className="text-sm font-black text-[#222222]">

                  {formatWeeklyCheckInDate(week.weekStart)}

                  {" – "}

                  {formatWeeklyCheckInDate(week.weekEnd)}

                </h3>

                <p className="mt-1 text-[11px] text-[#757575]">

                  Due {formatWeeklyCheckInDate(week.dueDate)}

                </p>

                <p className="mt-0.5 text-[10px] text-[#999999]">

                  Final deadline:{" "}

                  {formatWeeklyCheckInDate(week.graceEnd)}

                </p>

{week.status === "upcoming" && (

  <p className="mt-2 text-[11px] font-semibold text-[#FF914D]">

    Available from Friday{" "}

    {formatWeeklyCheckInDate(week.dueDate)}

  </p>

)}

              </div>

              <span

                className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-bold ${badgeClass}`}

              >

                {statusLabel}

              </span>

            </div>

            {isCompleted && submission && (

              <div className="mt-4 space-y-3">

                <div className="grid grid-cols-2 gap-2">

                  {[

                    ["Opening cash", submission.opening_cash_balance],

                    ["Total sales", submission.total_sales],

                    ["Cash received", submission.cash_sales_received],

                    ["Total spent", submission.total_spent],

                    ["Cash spent", submission.cash_spent],

                  ].map(([label, amount]) => (

                    <div

                      key={label}

                      className="rounded-xl bg-[#F7F7F7] p-3"

                    >

                      <p className="text-[10px] text-[#898989]">

                        {label}

                      </p>

                      <p className="mt-1 text-sm font-bold text-[#222222]">

                        {Number(amount).toLocaleString("en-ZA", {

                          style: "currency",

                          currency: "ZAR",

                        })}

                      </p>

                    </div>

                  ))}

                </div>

                <div className="rounded-xl bg-[#FFF0E6] p-4">

                  <p className="text-[11px] font-bold text-[#9B5835]">

                    Closing cash balance

                  </p>

                  <p className="mt-1 text-xl font-black text-[#222222]">

                    {Number(

                      submission.closing_cash_balance

                    ).toLocaleString("en-ZA", {

                      style: "currency",

                      currency: "ZAR",

                    })}

                  </p>

                </div>

                <p className="text-[10px] text-[#898989]">

                  Submitted:{" "}

                  {new Date(

                    submission.submitted_at

                  ).toLocaleString("en-ZA", {

                    timeZone: "Africa/Johannesburg",

                    dateStyle: "medium",

                    timeStyle: "short",

                  })}

                </p>

              </div>

            )}

            {isMissed && (

              <div className="mt-4 rounded-xl bg-[#FFF5F5] p-3">

                <p className="text-xs leading-relaxed text-[#C9474E]">

                  This financial check-in was missed.

                  The Wednesday submission deadline has passed.

                </p>

              </div>

            )}

            {canSubmit && (

              <button

                type="button"

                onClick={() => {

                  setSelectedWeeklyCheckInWeek(week.week_start);

                  setWeeklyCheckInOpen(true);

                }}

                className="mt-4 w-full rounded-xl bg-[#FF914D] py-3 text-xs font-black text-white"

              >

                {submission

                  ? "UPDATE CHECK-IN"

                  : "COMPLETE CHECK-IN"}

              </button>

            )}

          </article>

        );

      })}

  </section>

)}

        </div>
      </div>
    </AppScreenShell>
    <WeeklyFinancialCheckIn
      open={weeklyCheckInOpen}
      weekStart={selectedWeeklyCheckInWeek || undefined}
      onClose={() => { setWeeklyCheckInOpen(false); setSelectedWeeklyCheckInWeek(null); }}
      onSubmitted={() => setWeeklyHistoryRefreshKey((value) => value + 1)}
    />
    <SubscriptionPopup open={showSubscriptionPopup} onClose={() => setShowSubscriptionPopup(false)} />
  </>
  );
}
